import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, MapPin, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import LocationFormDialog from "@/components/provider/LocationFormDialog";
import { providerApi } from "@/lib/api/provider";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function LocationsPage() {
    const { t } = useI18n();
    const [items, setItems] = useState(null);
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [confirmDel, setConfirmDel] = useState(null);

    const refresh = async () => {
        try {
            const list = await providerApi.listLocations();
            setItems(list);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };
    useEffect(() => { refresh(); }, []);

    const save = ({ id, payload }) =>
        id ? providerApi.updateLocation(id, payload) : providerApi.createLocation(payload);

    const onDelete = async () => {
        try {
            await providerApi.deleteLocation(confirmDel.id);
            toast.success(t("provider.locations.deleted"));
            setConfirmDel(null);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="max-w-5xl space-y-6 fade-up" data-testid="page-provider-locations">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("provider.locations.title")}</h1>
                    <p className="mt-2 text-sm text-muted-foreground">{t("provider.locations.subtitle")}</p>
                </div>
                <Button onClick={() => setDialog({ open: true, initial: null })} data-testid="add-location-button">
                    <Plus className="h-4 w-4 me-1" />
                    {t("provider.locations.add")}
                </Button>
            </header>

            {items === null && (
                <div className="surface-card p-12 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            )}
            {items && items.length === 0 && (
                <div className="surface-card p-10 text-center" data-testid="locations-empty">
                    <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">{t("provider.locations.empty")}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t("provider.locations.emptyHint")}</p>
                </div>
            )}
            {items && items.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-4">
                    {items.map((loc) => (
                        <div key={loc.id} className="surface-card p-5 group" data-testid={`location-card-${loc.id}`}>
                            <div className="flex items-start gap-3">
                                <div className="icon-tile h-10 w-10 shrink-0">
                                    <MapPin className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-base font-semibold truncate">{loc.name_en}</h3>
                                        {!loc.is_active && <Badge variant="secondary" className="text-[10px]">{t("provider.locations.inactive")}</Badge>}
                                    </div>
                                    <p className="mt-0.5 text-xs text-muted-foreground truncate font-arabic" dir="rtl">{loc.name_ar}</p>
                                    <p className="mt-2 text-xs text-muted-foreground">
                                        {[loc.neighborhood, loc.city].filter(Boolean).join(", ")}
                                    </p>
                                    {loc.phone && <p className="text-xs text-muted-foreground" dir="ltr">{loc.phone}</p>}
                                    {loc.hours_text && <p className="mt-1 text-xs text-muted-foreground">{loc.hours_text}</p>}
                                </div>
                                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, initial: loc })} data-testid={`location-edit-${loc.id}`}>
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button size="icon" variant="ghost" onClick={() => setConfirmDel(loc)} data-testid={`location-delete-${loc.id}`}>
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <LocationFormDialog
                open={dialog.open}
                onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
                initial={dialog.initial}
                save={save}
                onSaved={() => refresh()}
            />
            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent data-testid="location-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("provider.locations.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("provider.locations.confirmDeleteBody", { name: confirmDel?.name_en || "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="location-delete-confirm-button">
                            {t("provider.locations.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
